import React from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

// Import service images
import drivewaysImg from "@/assets/images/services/driveways.png";
import foundationsImg from "@/assets/images/services/foundations.png";
import slabsImg from "@/assets/images/services/slabs.png";
import flooringImg from "@/assets/images/services/flooring.png";
import patiosImg from "@/assets/images/services/patios.png";
import demolitionImg from "@/assets/images/services/demolition.png";
import sidewalksImg from "@/assets/images/services/sidewalks.png";
import repairImg from "@/assets/images/services/repair.png";

export default function Services() {
  const { ref, visible } = useScrollReveal();

  const services = [
    {
      key: "driveways",
      name: "Concrete Driveways",
      desc: "Durable, professionally finished concrete driveways designed to improve access, appearance and long-term performance.",
      image: drivewaysImg
    },
    {
      key: "foundations",
      name: "Foundations",
      desc: "Concrete foundation solutions for residential and commercial construction projects.",
      image: foundationsImg
    },
    {
      key: "slabs",
      name: "Concrete Slabs",
      desc: "Quality concrete slabs for homes, garages, shops, additions and commercial applications.",
      image: slabsImg
    },
    {
      key: "flooring",
      name: "Concrete Flooring",
      desc: "Professional concrete flooring for residential, commercial and functional spaces.",
      image: flooringImg
    },
    {
      key: "patios",
      name: "Backyard Patios",
      desc: "Custom concrete patios designed to create durable, functional outdoor living spaces.",
      image: patiosImg
    },
    {
      key: "demolition",
      name: "Demolition",
      desc: "Concrete demolition and removal for renovation, replacement and new construction projects.",
      image: demolitionImg
    },
    {
      key: "sidewalks",
      name: "Sidewalks & Walkways",
      desc: "Concrete sidewalks and walkways built for safe, durable and attractive access.",
      image: sidewalksImg
    },
    {
      key: "repair",
      name: "Concrete Repair",
      desc: "Repair and replacement solutions for damaged, cracked or deteriorating concrete.",
      image: repairImg
    }
  ];

  return (
    <section id="services" className="bg-[hsl(var(--poured))] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div
          ref={ref}
          className={`text-center max-w-3xl mx-auto mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-0.5 bg-[#54575f]" />
            <span className="section-label text-[#194d32] text-base">
              WHAT WE DO
            </span>
            <div className="w-8 h-0.5 bg-[#434951]" />
          </div>
          <h2 className="headline text-[hsl(var(--navy))] text-4xl sm:text-5xl md:text-6xl">
            Concrete Solutions Built for Your Project
          </h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((service, i) => (
            <div
              key={service.key}
              className="group bg-white border border-[hsl(var(--concrete))] rounded-sm overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
                transition: `all 0.6s ease-out ${i * 0.08}s`
              }}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 md:grayscale md:group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--navy))]/40 to-transparent" />
                {/* Accent line on hover */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[hsl(var(--accent))] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </div>
              {/* Content */}
              <div className="p-5">
                <h3 className="font-heading text-xl text-[hsl(var(--navy))] tracking-wide mb-2">
                  {service.name}
                </h3>
                <p className="text-sm text-[hsl(var(--charcoal))]/70 leading-relaxed mb-4">
                  {service.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}